/** Shared study-group classification for project IDs. */

export interface StudyGroup {
  key: string;
  label: string;
  projects: string[];
}

/**
 * Matches any vN-additions project id (v3additions-*, v4additions-*, …),
 * so future regulatory augmentation rounds categorize without a code change.
 */
const VN_ADDITIONS_REGEX = /^v\d+additions-/;

function isAbdctProject(id: string): boolean {
  return id.startsWith("abdct-") || id.startsWith("abdo-triage-") || VN_ADDITIONS_REGEX.test(id);
}

/** Reporting time-study projects — both the legacy `reporting-*` naming
 *  and the newer `study-mode-*` slots (pilot + smoke). */
function isReportingProject(id: string): boolean {
  return id.startsWith("reporting-") || id.startsWith("study-mode-");
}

function isChestEvalProject(id: string): boolean {
  return id.startsWith("chest-eval-");
}

function isHeadEvalProject(id: string): boolean {
  return id.startsWith("head-eval-");
}

/**
 * Categorize project IDs into study groups by prefix.
 *
 * Used by the labeling category UI and AdminUserManagement to ensure
 * consistent grouping across the UI.
 */
export function groupProjects(projectIds: string[]): StudyGroup[] {
  const ot: string[] = [];
  const us3d: string[] = [];
  const hl3d: string[] = [];
  const abdct: string[] = [];
  const chestEval: string[] = [];
  const headEval: string[] = [];
  const reporting: string[] = [];
  const other: string[] = [];

  for (const id of projectIds) {
    if (id.startsWith("ot-v1-") || id.startsWith("ot-v2additions-")) ot.push(id);
    else if (id.startsWith("us3d-")) us3d.push(id);
    else if (id.startsWith("hl3d-")) hl3d.push(id);
    else if (isAbdctProject(id)) abdct.push(id);
    else if (isChestEvalProject(id)) chestEval.push(id);
    else if (isHeadEvalProject(id)) headEval.push(id);
    else if (isReportingProject(id)) reporting.push(id);
    else other.push(id);
  }

  const groups: StudyGroup[] = [];
  if (ot.length) groups.push({ key: "ot", label: "a2z-Omni-Triage", projects: ot.sort() });
  if (us3d.length)
    groups.push({ key: "us3d", label: "Urinary Stone 3D Annotations", projects: us3d.sort() });
  if (hl3d.length)
    groups.push({ key: "hl3d", label: "Hepatic Lesion 3D Annotations", projects: hl3d.sort() });
  if (abdct.length) groups.push({ key: "abdct", label: "a2z-Abdo-Triage", projects: abdct.sort() });
  if (chestEval.length)
    groups.push({ key: "chest-eval", label: "Chest Eval", projects: chestEval.sort() });
  if (headEval.length)
    groups.push({ key: "head-eval", label: "Head Eval", projects: headEval.sort() });
  if (reporting.length)
    groups.push({ key: "reporting", label: "Reporting Studies", projects: reporting.sort() });
  if (other.length) groups.push({ key: "other", label: "Other Projects", projects: other.sort() });
  return groups;
}

/**
 * Nest eval batches (chest-eval / head-eval) by task number, with reader/base
 * variants grouped under each task. Non-task batches (e.g. `-adjudication-*`)
 * collect into a trailing "Adjudication" group. Generic over any record with a
 * `project_id` so both `ProjectSummary` and plain ids work.
 */
export function groupEvalBatchesByTask<T extends { project_id: string }>(
  items: T[],
): { taskLabel: string; batches: T[] }[] {
  const TASK_RE = /-task-(\d+)(?:-reader[AB])?$/;
  const byTask = new Map<string, T[]>();
  const nonTask: T[] = [];
  for (const item of items) {
    const m = item.project_id.match(TASK_RE);
    if (m) {
      const key = `task-${m[1]}`;
      const bucket = byTask.get(key) ?? [];
      bucket.push(item);
      byTask.set(key, bucket);
    } else {
      nonTask.push(item);
    }
  }
  const byId = (a: T, b: T) => a.project_id.localeCompare(b.project_id);
  const tasks = [...byTask.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, batches]) => ({
      taskLabel: `Task ${key.replace("task-", "")}`,
      batches: batches.sort(byId),
    }));
  if (nonTask.length) tasks.push({ taskLabel: "Adjudication", batches: nonTask.sort(byId) });
  return tasks;
}

/** Check whether a project ID belongs to a known study prefix. */
export function isKnownStudyPrefix(projectId: string): boolean {
  return (
    projectId.startsWith("ot-v1-") ||
    projectId.startsWith("us3d-") ||
    projectId.startsWith("hl3d-") ||
    isAbdctProject(projectId) ||
    isReportingProject(projectId)
  );
}

/** Matches a US3D project ID and captures the phase + batch numbers. */
export const US3D_REGEX = /^us3d-phase(\d+)-batch(\d+)$/;

/**
 * Study-group keys that start COLLAPSED on /labeling-overview.
 * Edit this list to change defaults — keys must match those returned by groupProjects().
 */
export const OVERVIEW_DEFAULT_COLLAPSED_KEYS: readonly string[] = ["abdct", "other"];
